import type { ComponentProps, ReactNode } from "react";
import { Button } from "@kalodata/kds-design";
import { Trans } from "react-i18next";
import { twMerge } from "tailwind-merge";

const PRIMARY_BUTTON_CLASS = twMerge(
  "bg-kds-dark-reverse text-kds-light-reverse shadow-[0_1px_3px_0_var(--kds-shadow)]",
  "hover:bg-(--palette-slate-11) hover:text-kds-light-reverse",
);

export interface ClipModalFooterConfig {
  /** 左侧额外内容（提示 / 第三个按钮）。传入则底栏变 space-between，否则右对齐 */
  extra?: ReactNode;
  /** 是否展示取消按钮，默认 true */
  showCancel?: boolean;
  /** 取消按钮文案，默认 <Trans>Cancel</Trans> */
  cancelText?: ReactNode;
  cancelDisabled?: boolean;
  onCancel?: () => void;
  cancelButtonProps?: Partial<ComponentProps<typeof Button>>;
  /** 主按钮文案，默认 <Trans>OK</Trans> */
  confirmText?: ReactNode;
  confirmLoading?: boolean;
  confirmDisabled?: boolean;
  onConfirm?: () => void;
  confirmButtonProps?: Partial<ComponentProps<typeof Button>>;
  /** 覆盖根容器 className */
  className?: string;
}

/**
 * ClipModal 的统一底栏：贴边的 Cancel + Primary 操作栏。
 *
 * 通过 ClipModal 的 `footerConfig` 渲染，调用方只传数据（文案、回调、loading/disabled），
 * 无需在 children 内手写 `<div>` + KDS Button。需要完全自定义时改用 ClipModal 的 `footer`。
 *
 * 负边距 + border-t 与各 Editor 原 in-body footer 一致：相对 ClipModal 内容区
 * （container 的 p-[16px]）左右贴边、底部齐平。
 */
export const ClipModalFooter = ({
  extra,
  showCancel = true,
  cancelText,
  cancelDisabled,
  onCancel,
  cancelButtonProps,
  confirmText,
  confirmLoading,
  confirmDisabled,
  onConfirm,
  confirmButtonProps,
  className,
}: ClipModalFooterConfig) => {
  const { className: confirmButtonClassName, ...restConfirmButtonProps } = confirmButtonProps ?? {};

  return (
    <div
      className={twMerge(
        "-mx-[16px] -mb-[16px] flex items-center gap-[12px] border-t border-kds-border px-[16px] py-[16px]",
        extra ? "justify-between" : "justify-end",
        className,
      )}
    >
      {extra ? <div className="flex items-center">{extra}</div> : null}
      <div className="flex items-center gap-[12px]">
        {showCancel ? (
          <Button variant="outline" size="md" disabled={cancelDisabled} onClick={onCancel} {...cancelButtonProps}>
            {cancelText ?? <Trans>Cancel</Trans>}
          </Button>
        ) : null}
        <Button
          type="reverse"
          size="md"
          disabled={confirmDisabled || confirmLoading}
          loading={confirmLoading}
          onClick={onConfirm}
          className={twMerge(PRIMARY_BUTTON_CLASS, confirmButtonClassName)}
          {...restConfirmButtonProps}
        >
          {confirmText ?? <Trans>OK</Trans>}
        </Button>
      </div>
    </div>
  );
};
